import { GoogleOAuthProvider } from '@react-oauth/google';
import { useQueryClient } from '@tanstack/react-query';
import type { PropsWithChildren } from 'react';
import { createContext, useContext, useLayoutEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { LogoutEvent } from '@doger/core/shared/api';

import { GoogleOneTapAuth } from './google';

type AuthContextPayload = {
  signOutUrl: string;
  signInUrl: string;
};

export const AuthContext = createContext<AuthContextPayload>({
  signOutUrl: '/sign-in',
  signInUrl: '/sign-in',
});

export const useAuthContext = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }

  return context;
};

type AuthProviderProps = PropsWithChildren &
  AuthContextPayload & {
    GOOGLE_CLIENT_ID: string;
    disabledGoogleOneTapAuth?: boolean;
    onGoogleOneTapSuccess?: (data: {
      accessToken: string;
      refreshToken: string;
      isExistingUser: boolean;
    }) => void;
  };

export const AuthProvider = ({
  children,
  GOOGLE_CLIENT_ID,
  disabledGoogleOneTapAuth,
  onGoogleOneTapSuccess,
  ...value
}: AuthProviderProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  useLayoutEffect(() => {
    const handleLogout = () => {
      navigate(value.signOutUrl, {
        state: {
          from: location,
        },
      });

      setTimeout(() => {
        queryClient.resetQueries({ queryKey: ['auth'] });
      });
    };

    document.addEventListener(LogoutEvent.NAME, handleLogout);

    return () => {
      document.removeEventListener(LogoutEvent.NAME, handleLogout);
    };
  }, [value.signOutUrl, location, queryClient, navigate]);

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AuthContext.Provider value={value}>
        {children}

        <GoogleOneTapAuth
          disabled={disabledGoogleOneTapAuth}
          onSuccess={onGoogleOneTapSuccess}
        />
      </AuthContext.Provider>
    </GoogleOAuthProvider>
  );
};
